import { Link } from "gatsby"
import React, { useLayoutEffect, useRef } from "react"

const HeroSolutions = ({
  title,
  subtitle,
  heroImg,
  partnerLogos = [],
  cta,
}) => {
  const bodyRef = useRef(null)
  const imgWrapRef = useRef(null)
  const ctaMobileRef = useRef(null)

  // useLayoutEffect: computes a stable mobile image size on mount and on resize/orientation, storing it in a CSS var (no work on scroll).
  useLayoutEffect(() => {
    const clamp = (v, min, max) => Math.round(Math.min(Math.max(v, min), max))

    const updateImageSize = () => {
      const hero = bodyRef.current
      const imgWrap = imgWrapRef.current
      if (!hero || !imgWrap) return

      const viewportH = window.innerHeight
      const headerH = 70
      const heroH = viewportH - headerH

      const heroTop = hero.getBoundingClientRect().top
      const imgTop = imgWrap.getBoundingClientRect().top - heroTop
      const ctaH = ctaMobileRef.current
        ? ctaMobileRef.current.getBoundingClientRect().height
        : 0

      const gap = 16
      const availableH = Math.max(160, heroH - imgTop - ctaH - gap)
      const targetW = clamp(availableH, 260, 560)

      hero.style.setProperty("--hero-img-w", `${targetW}px`)
    }

    const onResize = () => updateImageSize()

    updateImageSize()

    window.addEventListener("resize", onResize, { passive: true })
    window.addEventListener("orientationchange", onResize, { passive: true })

    const imgEl = imgWrapRef.current?.querySelector("img")
    if (imgEl) imgEl.addEventListener("load", onResize, { once: true })

    return () => {
      window.removeEventListener("resize", onResize)
      window.removeEventListener("orientationchange", onResize)
    }
  }, [])

  return (
    <section className="hero has-background-dark hero--bleed">
      <div className="hero-body" ref={bodyRef}>
        <div className="container px-5-desktop">
          <div className="columns is-vcentered">
            <div className="column is-12-tablet is-8-desktop hero--bleed-text">
              <div className="columns is-marginless">
                <div className="column is-paddingless is-12-mobile is-12-tablet is-8-desktop is-10-widescreen">
                  <h1
                    className="is-size-1-desktop is-size-1-tablet has-text-weight-medium has-text-white is-size-3-mobile has-line-height-2"
                    style={{ marginTop: 0 }}
                  >
                    {title}
                  </h1>
                </div>
                <div className="column is-paddingless is-hidden-mobile" />
              </div>

              <div className="column is-paddingless is-12-mobile is-12-tablet is-8-desktop is-10-widescreen">
                <p className="hero-subtitle is-size-4-tablet my-6-desktop my-5-touch is-size-6-mobile is-size-5-desktop">
                  {subtitle}
                </p>
              </div>

              <div className="partner-logo-row mt-2 mb-3 animated fadeIn">
                {partnerLogos.map(({ src, alt, width }, i) => (
                  <div key={i} className="partner-logo-cell">
                    <img
                      src={src}
                      alt={alt}
                      className="partner-logo"
                      style={{ "--logo-width": (width || 120) + "px" }}
                    />
                  </div>
                ))}
              </div>

              <div
                className="hero-image-inline is-hidden-desktop"
                ref={imgWrapRef}
              >
                <img src={heroImg} alt="Hero" />
              </div>

              {cta && (
                <div
                  className="hero-cta-mobile is-hidden-desktop is-flex is-justify-content-center is-align-items-center"
                  ref={ctaMobileRef}
                >
                  <Link
                    to={cta.href}
                    className="button is-rounded is-link is-size-6 is-size-5-tablet"
                    target={cta.target || "_self"}
                  >
                    {cta.label}
                  </Link>
                </div>
              )}

              {cta && (
                <div className="is-flex is-align-items-center is-justify-content-start mt-6 py-6 is-hidden-touch">
                  <Link
                    to={cta.href}
                    className="animated fadeIn button is-rounded is-medium is-link px-6"
                    style={{ animationDelay: "500ms" }}
                    target={cta.target || "_self"}
                  >
                    {cta.label}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <img
        className="hero-bleed-media is-hidden-touch is-hidden-mobile"
        src={heroImg}
        alt="Hero"
      />
    </section>
  )
}

export default HeroSolutions
